import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { apiFetch, ApiError } from "@/lib/api";
import {
  type DeviceDrawingCoverageGap,
  type DrawingCoverageDocument,
  type DrawingCoverageStatus,
} from "@/lib/documents";
import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { PageTitleWithHelp, HoverHintTrigger } from "@/components/ui/title-help";
import { Button } from "@/components/ui/Button";

const QUERY_KEY = ["document-drawing-coverage"] as const;
const DRAWING_COVERAGE_SEARCH_DEBOUNCE_MS = 300;

type MissingFilter = "either" | "2d" | "3d";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function statusLabel(status: DrawingCoverageStatus): string {
  if (status === "approved") return "Approved";
  if (status === "wip") return "WIP only";
  return "Missing";
}

function statusTone(status: DrawingCoverageStatus): string {
  if (status === "approved") return "text-green-700 bg-green-50";
  if (status === "wip") return "text-sky-800 bg-sky-50";
  return "text-amber-800 bg-amber-50";
}

function drawingHoverRows(document: DrawingCoverageDocument): { label: string; value: string }[] {
  const source = document.on_sku
    ? "Part number"
    : document.collection_codes.length > 0
      ? `Collection: ${document.collection_codes.join(", ")}`
      : "Collection";
  return [
    { label: "Title", value: document.title.trim() || "Untitled" },
    { label: "Revision", value: document.revision?.trim() || "—" },
    { label: "File", value: document.original_filename?.trim() || "—" },
    { label: "Target", value: source },
  ];
}

function DrawingStatusCell(props: {
  status: DrawingCoverageStatus;
  document: DrawingCoverageDocument | null;
}): JSX.Element {
  const pill = (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${statusTone(props.status)}`}>
      {statusLabel(props.status)}
    </span>
  );
  if (props.status === "missing" || props.document == null) {
    return pill;
  }
  return (
    <HoverHintTrigger
      closeOnTriggerActivate
      trigger={
        <Link
          to={`/documents/workbench?document=${encodeURIComponent(props.document.id)}`}
          className={`inline-flex rounded px-2 py-0.5 text-xs font-medium hover:underline ${statusTone(props.status)}`}
        >
          {statusLabel(props.status)}
        </Link>
      }
    >
      <HoverHintDefinitionGrid rows={drawingHoverRows(props.document)} />
    </HoverHintTrigger>
  );
}

function formatDescription(row: DeviceDrawingCoverageGap): string {
  return row.mv_description?.trim() || row.product_name?.trim() || "—";
}

type DrawingGapSortKey = "sku" | "description" | "drawing_2d_status" | "drawing_3d_status";

const DRAWING_COVERAGE_COLUMNS: HubDataTableColumn<DeviceDrawingCoverageGap, DrawingGapSortKey>[] = [
  {
    id: "sku",
    header: "Part number",
    sortKey: "sku",
    cellClassName: "font-mono text-xs sm:text-sm",
    sortValue: (row) => row.sku,
    cell: (row) => row.sku,
  },
  {
    id: "description",
    header: "Description",
    sortKey: "description",
    sortValue: (row) => formatDescription(row),
    cell: (row) => formatDescription(row),
  },
  {
    id: "drawing_2d_status",
    header: "2D drawing",
    sortKey: "drawing_2d_status",
    sortValue: (row) => row.drawing_2d_status,
    cell: (row) => <DrawingStatusCell status={row.drawing_2d_status} document={row.drawing_2d} />,
  },
  {
    id: "drawing_3d_status",
    header: "3D drawing",
    sortKey: "drawing_3d_status",
    sortValue: (row) => row.drawing_3d_status,
    cell: (row) => <DrawingStatusCell status={row.drawing_3d_status} document={row.drawing_3d} />,
  },
  {
    id: "upload",
    header: "Upload document",
    sortValue: () => "",
    cell: (row) => {
      const showUpload = row.drawing_2d_status === "missing" || row.drawing_3d_status === "missing";
      return showUpload ? (
        <Link
          to={`/documents/workbench?sku=${encodeURIComponent(row.sku)}&upload=1`}
          className="text-sm text-blue-700 hover:underline"
        >
          Upload document
        </Link>
      ) : null;
    },
  },
];

export function DocumentDrawingCoveragePage() {
  const [search, setSearch] = useState("");
  const [missingFilter, setMissingFilter] = useState<MissingFilter>("either");
  const [countWipAsCovered, setCountWipAsCovered] = useState(true);
  const debouncedSearch = useDebouncedValue(search.trim(), DRAWING_COVERAGE_SEARCH_DEBOUNCE_MS);
  const searchPending = search.trim() !== debouncedSearch;

  const coverageQuery = useQuery<{ gaps: DeviceDrawingCoverageGap[]; total: number }, ApiError>({
    queryKey: [...QUERY_KEY, debouncedSearch, missingFilter, countWipAsCovered],
    queryFn: () =>
      apiFetch<{ gaps: DeviceDrawingCoverageGap[]; total: number }>("/api/document-drawing-coverage", {
        query: {
          q: debouncedSearch || undefined,
          missing: missingFilter,
          countWip: countWipAsCovered ? "true" : "false",
        },
      }),
    placeholderData: keepPreviousData,
  });

  const gaps = coverageQuery.data?.gaps ?? [];
  const missing2dCount = useMemo(
    () => gaps.filter((row) => row.drawing_2d_status === "missing").length,
    [gaps],
  );
  const missing3dCount = useMemo(
    () => gaps.filter((row) => row.drawing_3d_status === "missing").length,
    [gaps],
  );
  const wip2dCount = useMemo(
    () => gaps.filter((row) => row.drawing_2d_status === "wip").length,
    [gaps],
  );
  const wip3dCount = useMemo(
    () => gaps.filter((row) => row.drawing_3d_status === "wip").length,
    [gaps],
  );

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Lists custom devices (not catalog / COTS) that still need a 2D or 3D drawing. Work in progress counts as coverage unless you turn that off. Service, tool, label, and similar out-of-scope stock keeping units are omitted. A drawing targeted to a product collection counts for every member of that collection, including template-driven membership.">
        Drawing coverage gaps
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="drawing-coverage-search" className="block text-sm font-medium text-slate-700">
              Search devices
            </label>
            <Input
              id="drawing-coverage-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by part number or description..."
              aria-busy={searchPending || coverageQuery.isFetching}
            />
          </div>
          <div className="space-y-2">
            <span className="block text-sm font-medium text-slate-700">Missing drawing</span>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ["either", "Missing either"],
                  ["2d", "Missing 2D"],
                  ["3d", "Missing 3D"],
                ] as const
              ).map(([value, label]) => (
                <Button
                  key={value}
                  size="sm"
                  variant={missingFilter === value ? "primary" : "secondary"}
                  onClick={() => setMissingFilter(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={countWipAsCovered}
                onChange={(event) => setCountWipAsCovered(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              Count work in progress as covered
            </label>
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={
          coverageQuery.isLoading
            ? "Loading gaps..."
            : `${gaps.length.toLocaleString()} device${gaps.length === 1 ? "" : "s"} match these filters`
        }
        headerExtra={
          !coverageQuery.isLoading && gaps.length > 0 ? (
            <p className="shrink-0 text-sm text-slate-600">
              Missing 2D: {missing2dCount.toLocaleString()} · Missing 3D: {missing3dCount.toLocaleString()}
              {countWipAsCovered
                ? null
                : ` · WIP only 2D: ${wip2dCount.toLocaleString()} · WIP only 3D: ${wip3dCount.toLocaleString()}`}
            </p>
          ) : null
        }
        columns={DRAWING_COVERAGE_COLUMNS}
        rows={gaps}
        rowKey={(row) => row.sku}
        loading={coverageQuery.isLoading}
        loadingMessage="Loading drawing coverage..."
        error={coverageQuery.error != null ? coverageQuery.error.message : null}
        empty={
          countWipAsCovered
            ? "No devices match these filters. Every in-scope custom device has a 2D and 3D drawing (released or work in progress, including via product collection)."
            : "No devices match these filters. Every in-scope custom device has a released 2D and 3D drawing (including via product collection)."
        }
        isFiltered={debouncedSearch !== "" || missingFilter !== "either" || !countWipAsCovered}
        filteredEmpty="No devices match these filters."
      />
      {!coverageQuery.isLoading && gaps.length > 0 ? (
        <p className="text-sm text-slate-600">
          {countWipAsCovered
            ? "Work in progress already counts as covered. These rows still have no usable 2D and/or 3D drawing."
            : "Only released drawings count as covered."}
        </p>
      ) : null}
    </div>
  );
}
